/*
The worker password reset page will be used to reset the password of a worker.
Redux will be used to manage the state of the page. 
The redux states to track are: 
workerUserToken, codeSentStatus, varificationStatus

workerUserToke: the token that is obtained after the worker has been verified, the token is received from the backend. 
codeStatus: the current status of the code. Values can be: 'idle', 'sent', 'error, 'loading', 'notFound'

idle:   the default state of the codeSentStatus, the code has not been sent.
sent:   A matching phone number has been found and a code has been sent to the worker's phone number.
notFound:   The phone number was not found in the database.
error:  An error has occured while sending the code.
*/


import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { sendCode, verifyCode, changePassword } from '../../features/passwordResetWorkers/passwordResetWorkersSlice';


function WorkerPasswordReset() {

    const [phoneValue, setPhoneValue] = useState('');
    const [codeValue, setCodeValue] = useState('');

    const [sentStatus, setSentStatus] = useState(false);

    // Modal state and password fields
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [verifyPassword, setVerifyPassword] = useState('');

    //Dispatch  is used to call an action from the redux store. 
    const dispatch = useDispatch();   
    const navigate = useNavigate();

    //Redux States
    const{message} = useSelector(state => state.workerPasswordReset);
    const {verificationStatus, token} = useSelector(state => state.workerPasswordReset.verification)
    
    useEffect(() => {
        if (message) {
            toast.info(message);
        }
    }, [message]);

    // Open modal when verification is approved and token exists
    useEffect(() => {
        if (verificationStatus === "approved" && token) {
            setShowPasswordModal(true);
        }
    }, [verificationStatus, token]);

    const handleResendCode = () => {
    // Add logic to handle resending the code
        toast.info(`If registered, code will be re-sent to ${phoneValue}`);
    };

    const sendPhoneVerification = (e) => {
        e.preventDefault();
        // Remove all dashes before dispatching
        const phoneNoDashes = phoneValue.replace(/-/g, '');
        dispatch(sendCode(phoneNoDashes));
        // Optionally, you can show a toast after dispatch if needed
       
        setSentStatus(true);
    };

    const formatPhoneNumber = (value) => {
        const phoneNumber = value.replace(/[^\d]/g, '');
        const phoneNumberLength = phoneNumber.length;

        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}`;
        return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const handlePhoneChange = (e) => {
        const formattedPhoneNumber = formatPhoneNumber(e.target.value);
        setPhoneValue(formattedPhoneNumber);
    };

    const handleCodeSubmit = (e) => {
        e.preventDefault();
        // Remove dashes from phone number before sending
        const phoneNoDashes = phoneValue.replace(/-/g, '');
        // Dispatch verifyCode with correct variable names for backend
        dispatch(verifyCode({ workerPhone: phoneNoDashes, code: codeValue }));
    };

    // Handle password modal submit
    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (newPassword !== verifyPassword) {
            toast.error("Passwords do not match");
            return;
        }
        // Dispatch password reset action here with token and newPassword
        try {
            const resultAction = await dispatch(changePassword(newPassword));
            if (changePassword.fulfilled.match(resultAction)) {
                toast.success("Password reset successful!");
                setShowPasswordModal(false);
                navigate('/workerLogin');
            } else {
                toast.error(resultAction.payload || "Password reset failed.");
            }
        } catch (err) {
            toast.error("Password reset failed.");
        }
    };

  return (
    <>
      <h1 className="text-2xl font-kanit text-center text-charcoal mb-8">Worker password reset</h1>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal bg-opacity-60">
          <div className="bg-ivory border-2 border-tan rounded-xl shadow-xl p-8 w-full max-w-md relative">
            <h2 className="text-xl font-kanit text-charcoal mb-4 text-center">Set New Password</h2>
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4">
              <input
                type="password"
                className="bg-sage border border-tan rounded-lg py-2 px-4 text-charcoal font-sans focus:outline-none focus:ring-2 focus:ring-robin"
                placeholder="New Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
              />
              <input
                type="password"
                className="bg-sage border border-tan rounded-lg py-2 px-4 text-charcoal font-sans focus:outline-none focus:ring-2 focus:ring-robin"
                placeholder="Verify New Password"
                value={verifyPassword}
                onChange={e => setVerifyPassword(e.target.value)}
                required
              />
              <button
                type="submit"
                className="bg-orangem text-white font-kanit py-2 px-6 rounded-lg shadow hover:bg-robin transition"
              >
                Submit New Password
              </button>
            </form>
            <button
              className="absolute top-2 right-3 text-charcoal hover:text-orangem text-xl"
              onClick={() => setShowPasswordModal(false)}
              aria-label="Close"
              type="button"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {!sentStatus && (
        <form 
          onSubmit={sendPhoneVerification} 
          className="max-w-md mx-auto p-6 rounded-xl shadow-lg bg-ivory border border-tan"
        >
          <div className="flex flex-col justify-center items-center">
            <label className="text-md font-kanit mb-2 text-charcoal" htmlFor="phoneValue">
              Phone number
            </label>
            <input
              className="bg-sage rounded-lg w-full md:w-2/3 py-2 px-4 text-charcoal font-sans border border-tan focus:outline-none focus:ring-2 focus:ring-robin transition"
              type="text"
              id="phoneValue"
              pattern="\d{3}-\d{3}-\d{4}"
              maxLength="12"
              required
              value={phoneValue}
              onChange={handlePhoneChange}
            />
          </div>
          <div className="flex justify-center">
            <button
              className="bg-orangem text-white font-kanit py-2 px-6 rounded-lg mt-6 shadow hover:bg-robin transition"
              type="submit"
            >
              Send Code
            </button>
          </div>
        </form>
      )}

      {/* Show this form if the status is true  */}
      {sentStatus && (
        <form 
          onSubmit={handleCodeSubmit} 
          className="max-w-md mx-auto p-6 rounded-xl shadow-lg bg-ivory border border-tan"
        >
          <div className="flex flex-col justify-center items-center">
            <input
              className="bg-sage rounded-lg w-full md:w-2/3 py-2 px-4 text-charcoal font-sans border border-tan focus:outline-none focus:ring-2 focus:ring-robin transition"
              type="text"
              id="codeValue"
              pattern="\d{6}"
              maxLength="6"
              required
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value)}
              placeholder='Enter code'
            />
          </div>

          <div className="flex flex-col items-center space-y-4 mt-6">
            <button
              className="bg-orangem text-white py-2 px-6 rounded-lg font-kanit shadow hover:bg-robin transition"
              type="submit"
            >
              Verify code
            </button>
            <button
              className="bg-teal text-ivory py-2 px-6 rounded-lg font-kanit shadow hover:bg-robin transition"
              type="button"
              onClick={handleResendCode}
            >
              Re-send code
            </button>
          </div>
        </form>
      )}

      {/* Modal to appear on successfull code verification */}
    </>
  );
}

export default WorkerPasswordReset;